/** 직무발명 state — node `libs/invention.js` 와 동일 */
export const INVENTION_STATE = {
  PENDING: 0,
  ONGOING: 1,
  APPLY: 2,
  REJECT: 3,
  DEPT_APPROVAL_PENDING: 4,
  DEPT_APPROVED: 5,
  DEPT_REJECTED: 6,
  REVIEW_PENDING: 7,
  REVIEW_IN_PROGRESS: 8,
  REVIEW_APPROVED: 9,
  REVIEW_REJECTED: 10,
  REVIEW_IN_PERSON: 11,
  /** 출연연(organization_type=2) 전용 */
  SURVEY_REQUESTED: 12,
  SURVEY_COMPLETED: 13,
};

/** 출연연 등 심의·조사 UI organization_type */
export const ORGANIZATION_TYPE_APPROVAL_UI = 2;

export const INVENTION_STATE_LABELS = {
  [INVENTION_STATE.PENDING]: "발명신고서 접수",
  [INVENTION_STATE.ONGOING]: "사건 등록",
  [INVENTION_STATE.APPLY]: "종료",
  [INVENTION_STATE.REJECT]: "폐기",
  /** 부서장 결재는 현재 프로세스에서 제외 — 이력·레거시 건 표시용만 유지 */
  [INVENTION_STATE.DEPT_APPROVAL_PENDING]: "부서장 승인 대기",
  [INVENTION_STATE.DEPT_APPROVED]: "부서장 승인",
  [INVENTION_STATE.DEPT_REJECTED]: "부서장 반려",
  [INVENTION_STATE.REVIEW_PENDING]: "심의 대기",
  [INVENTION_STATE.REVIEW_IN_PROGRESS]: "심의 진행 중",
  [INVENTION_STATE.REVIEW_APPROVED]: "심의 완료",
  [INVENTION_STATE.REVIEW_REJECTED]: "심의 반려",
  [INVENTION_STATE.REVIEW_IN_PERSON]: "보완요청(대면심의)",
  [INVENTION_STATE.SURVEY_REQUESTED]: "선행기술조사 요청",
  [INVENTION_STATE.SURVEY_COMPLETED]: "선행기술조사 완료",
};

/** 부서장 결재 state — UI 필터·상태변경 셀렉트에서 제외 */
export const INVENTION_DEPT_STATE_VALUES = [
  INVENTION_STATE.DEPT_APPROVAL_PENDING,
  INVENTION_STATE.DEPT_APPROVED,
  INVENTION_STATE.DEPT_REJECTED,
];

/** 리스트 필터용 (인덱스 = state 값, 비어 있는 칸은 사용 안 함) */
export const INVENTION_STATE_FIELD_TYPE = [
  INVENTION_STATE_LABELS[0],
  INVENTION_STATE_LABELS[1],
  INVENTION_STATE_LABELS[2],
  INVENTION_STATE_LABELS[3],
  INVENTION_STATE_LABELS[4],
  INVENTION_STATE_LABELS[5],
  INVENTION_STATE_LABELS[6],
  INVENTION_STATE_LABELS[7],
  INVENTION_STATE_LABELS[8],
  INVENTION_STATE_LABELS[9],
  INVENTION_STATE_LABELS[10],
  INVENTION_STATE_LABELS[11],
  INVENTION_STATE_LABELS[12],
  INVENTION_STATE_LABELS[13],
];

export const INVENTION_STATE_OPTIONS = INVENTION_STATE_FIELD_TYPE.map(
  (label, value) => ({ value, label })
).filter((o) => Boolean(o.label));

/**
 * 상세 페이지 상태 변경 셀렉트 옵션 (업무 흐름 순).
 * - 부서장(4~6) 제외 (현재 프로세스 미사용)
 * - 출연연이 아니면 심의·조사 상태(7~13) 제외
 */
export function getInventionStateEditOptions(
  organizationType,
  { researchInstituteType = ORGANIZATION_TYPE_APPROVAL_UI } = {}
) {
  const isResearchInstitute =
    Number(organizationType) === Number(researchInstituteType);
  const reviewSet = new Set(INVENTION_REVIEW_STATE_VALUES);
  const deptSet = new Set(INVENTION_DEPT_STATE_VALUES);
  return INVENTION_STATE_FILTER_DISPLAY_ORDER.filter((value) => {
    if (!INVENTION_STATE_LABELS[value]) return false;
    if (deptSet.has(value)) return false;
    if (!isResearchInstitute && reviewSet.has(value)) return false;
    return true;
  }).map((value) => ({
    value,
    label: INVENTION_STATE_LABELS[value],
  }));
}

export const convertInventionStateToStr = (state) => {
  const key = Number(state);
  if (!Number.isFinite(key)) return undefined;
  return INVENTION_STATE_LABELS[key];
};

/** 심의·조사 관련 state — 출연연(organization_type=2)에서만 필터 노출 */
export const INVENTION_REVIEW_STATE_VALUES = [
  INVENTION_STATE.SURVEY_REQUESTED,
  INVENTION_STATE.SURVEY_COMPLETED,
  INVENTION_STATE.REVIEW_PENDING,
  INVENTION_STATE.REVIEW_IN_PROGRESS,
  INVENTION_STATE.REVIEW_APPROVED,
  INVENTION_STATE.REVIEW_REJECTED,
  INVENTION_STATE.REVIEW_IN_PERSON,
];

export const INVENTION_REVIEW_STATUS_FILTER_LABELS = [
  "선행기술조사 요청",
  "선행기술조사 완료",
  "심의 대기",
  "심의 진행 중",
  "심의 완료",
  "심의 반려",
  "보완요청(대면심의)",
];

/**
 * 목록 「현재상태」 필터 표시 순서 (업무 흐름).
 * API DISTINCT 숫자순(0→1→2→7→12)이 아니라 이 순서로 노출.
 */
export const INVENTION_STATE_FILTER_DISPLAY_ORDER = [
  INVENTION_STATE.PENDING, // 발명신고서 접수
  INVENTION_STATE.SURVEY_REQUESTED, // 선행기술조사 요청
  INVENTION_STATE.SURVEY_COMPLETED, // 선행기술조사 완료
  INVENTION_STATE.REVIEW_PENDING, // 심의 대기
  INVENTION_STATE.REVIEW_IN_PROGRESS, // 심의 진행 중
  INVENTION_STATE.REVIEW_APPROVED, // 심의 완료
  INVENTION_STATE.REVIEW_REJECTED, // 심의 반려
  INVENTION_STATE.REVIEW_IN_PERSON, // 보완요청(대면심의)
  // 부서장(4~6) — 현재 프로세스 제외 (IPMS 동일)
  INVENTION_STATE.ONGOING, // 사건 등록
  INVENTION_STATE.APPLY, // 종료
  INVENTION_STATE.REJECT, // 폐기
];

/**
 * 발명 목록 「현재상태」 필터용 state 값 목록.
 * `/field` DISTINCT 결과에 있는 값은 출연연 여부와 무관하게 노출
 * (테이블에 심의·조사 상태가 있는데 드롭다운만 숨기면 UX 불일치).
 * 부서장(4~6)은 프로세스 제외로 필터에서 숨김.
 * 반환 순서는 INVENTION_STATE_FILTER_DISPLAY_ORDER.
 */
export function getInventionStateFilterValues(
  fieldStates,
  _organizationType,
  { researchInstituteType: _researchInstituteType = ORGANIZATION_TYPE_APPROVAL_UI } = {}
) {
  const raw = Array.isArray(fieldStates) ? fieldStates : [];
  const deptSet = new Set(INVENTION_DEPT_STATE_VALUES);
  const values = raw
    .map((v) => Number(v))
    .filter((v) => Number.isFinite(v) && !deptSet.has(v));
  const orderIndex = new Map(
    INVENTION_STATE_FILTER_DISPLAY_ORDER.map((v, i) => [v, i])
  );
  const unique = [...new Set(values)];
  return unique.sort((a, b) => {
    const ai = orderIndex.has(a) ? orderIndex.get(a) : 999;
    const bi = orderIndex.has(b) ? orderIndex.get(b) : 999;
    if (ai !== bi) return ai - bi;
    return a - b;
  });
}

/** 일반: 0·11 → 심의대기 / 출연연: 13(선행기술조사 완료)만 → 심의대기 */
export const BATCH_REVIEW_PENDING_ELIGIBLE_STATES = [
  INVENTION_STATE.PENDING,
  INVENTION_STATE.REVIEW_IN_PERSON,
];
export const BATCH_REVIEW_PENDING_ELIGIBLE_STATES_APPROVAL_UI = [
  INVENTION_STATE.SURVEY_COMPLETED,
];

export function getBatchReviewPendingEligibleStates(
  organizationType,
  { researchInstituteType = ORGANIZATION_TYPE_APPROVAL_UI } = {}
) {
  if (Number(organizationType) === Number(researchInstituteType)) {
    return BATCH_REVIEW_PENDING_ELIGIBLE_STATES_APPROVAL_UI;
  }
  return BATCH_REVIEW_PENDING_ELIGIBLE_STATES;
}

export const isBatchReviewPendingEligibleState = (
  state,
  organizationType,
  options
) =>
  getBatchReviewPendingEligibleStates(organizationType, options).includes(
    Number(state)
  );

/**
 * 일괄 선행기술조사 유/무·요청 대상 상태
 * - 공통: 발명신고서 접수(0)
 * - 출연연: 선행기술조사 완료(13) 재요청 가능
 */
export const BATCH_PRIOR_ART_ELIGIBLE_STATES = [INVENTION_STATE.PENDING];
export const BATCH_PRIOR_ART_ELIGIBLE_STATES_APPROVAL_UI = [
  INVENTION_STATE.PENDING,
  INVENTION_STATE.SURVEY_COMPLETED,
];

export function getBatchPriorArtEligibleStates(
  organizationType,
  { researchInstituteType = ORGANIZATION_TYPE_APPROVAL_UI } = {}
) {
  if (Number(organizationType) === Number(researchInstituteType)) {
    return BATCH_PRIOR_ART_ELIGIBLE_STATES_APPROVAL_UI;
  }
  return BATCH_PRIOR_ART_ELIGIBLE_STATES;
}

export const isBatchPriorArtEligibleState = (
  state,
  organizationType,
  options
) =>
  getBatchPriorArtEligibleStates(organizationType, options).includes(
    Number(state)
  );

/** 상태별 뱃지 톤 (목록·상세) */
export function getInventionStateBadgeTone(state) {
  const n = Number(state);
  if (n === INVENTION_STATE.SURVEY_REQUESTED) return "amber";
  if (n === INVENTION_STATE.SURVEY_COMPLETED) return "emerald";
  return null;
}
